import type { Scale } from '@tunasong/schemas'
import type { Chord, ChordVariant } from '../chord/index.js'
import { isChordNotes } from '../chord/chord-types.js'
import { getNoteChromaticDistance } from '../note/note-lib.js'
import { getDiatonicFunction } from './diatonic.js'
import type { ChordFunction } from './types.js'

/**
 * Map from the *target* chord's diatonic function (the function of `next`) to
 * the corresponding tritone-substitution label. e.g. when `next` resolves to
 * I, the current dom7 chord a tritone above I is labelled `subV/I`.
 *
 * Only the 5 supported targets (I, ii, IV, V, vi) appear here — every entry
 * in this map is also a defined member of `ChordFunction` (`subV/I`,
 * `subV/ii`, …).
 */
const SUB_V_BY_TARGET: Partial<Record<ChordFunction, ChordFunction>> = {
  I: 'subV/I',
  ii: 'subV/ii',
  IV: 'subV/IV',
  V: 'subV/V',
  vi: 'subV/vi',
}

/**
 * Dominant-7 variant set, mirroring `DOMINANT_7_VARIANTS` in `diatonic.ts`.
 * A tritone substitute is a dom7 chord by definition (the b7 of the
 * substitute is the third of the original V, and vice versa). We accept the
 * altered forms too — `D♭7♯9` resolving to C is still `subV/I` in practice.
 */
const DOMINANT_7_VARIANTS: ReadonlySet<string> = new Set(['7', '7#5', '7b5', '7#9', '7b9'])

/**
 * Returns `true` when `chord` is a dom7 variant. Mirror of
 * `isDominantSeventhVariant` in `diatonic.ts` but local-scoped so this module
 * stays independent.
 */
const isDom7 = (chord: Chord): chord is ChordVariant => {
  if (isChordNotes(chord)) {
    return false
  }
  return DOMINANT_7_VARIANTS.has(chord.variant)
}

/**
 * Detect a tritone substitution. Returns a `subV/X` label when:
 *
 *   1. `chord` is a dominant-7 (incl. altered: `7#9`, `7b9`, `7#5`, `7b5`)
 *   2. `next` is provided and resolves to one of I / ii / IV / V / vi in
 *      `scale` (via `getDiatonicFunction`)
 *   3. `chord.root` is exactly 6 semitones (a tritone) above `next.root`
 *
 * Without `next` we cannot disambiguate a tritone sub from an unrelated
 * chromatic dom7 — the spec deliberately requires `next` (see "Out-of-band
 * concerns" in `2026-05-14-style-aware-jazz.md`). When `next` is absent or
 * the conditions don't hold, return `null` and let the analyzer fall through
 * to its other branches (secondary dominant → chromatic).
 *
 * Why root-distance vs. root-of-`next` and not `chord.root + 6 ≡ scaleDegree`:
 * the substitution rule is defined relative to the *resolution target*, not
 * the key. `D♭7 → Fm` in C major is still a valid tritone sub of `V/ii`,
 * even though Fm isn't the tonic — the spec lists subV/ii precisely for that
 * case.
 */
export const getTritoneSubFunction = (
  chord: Chord,
  scale: Scale,
  next: Chord | undefined
): ChordFunction | null => {
  if (!next) {
    return null
  }
  if (!isDom7(chord)) {
    return null
  }
  if (isChordNotes(next)) {
    // Can't read .root off a ChordNotes — no resolution-target information.
    return null
  }
  const nextFunction = getDiatonicFunction(next, scale)
  if (!nextFunction) {
    return null
  }
  const subVLabel = SUB_V_BY_TARGET[nextFunction]
  if (!subVLabel) {
    return null
  }
  const semitones = getNoteChromaticDistance(next.root, chord.root)
  if (semitones !== 1) {
    return null
  }
  return subVLabel
}
